import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';

const TicketsManagement = ({
  tickets = [],
  ticketFilters = {
    sort_by: 'created_at',
    sort_order: 'desc',
    status_filter: '',
    priority_filter: '',
    assigned_to_filter: '',
    created_by_filter: '',
    search: ''
  },
  setTicketFilters = () => {},
  fetchTickets = () => {},
  assignableUsers = [],
  handleTicketClick = () => {},
  setSelectedTicket = () => {},
  setTicketToClose = () => {},
  setShowCloseModal = () => {},
  setTicketForNote = () => {},
  setShowNoteModal = () => {},
  downloadFile = () => {},
  API_URL = '',
  user = {},
  ticketForm = { title: '', description: '', priority: 'medium', assigned_to: '' },
  setTicketForm = () => {},
  selectedFile = null,
  setSelectedFile = () => {},
  handleCreateTicket = () => {},
  initialOpenCreate = false
}) => {
  const [showCreateModal, setShowCreateModal] = useState(initialOpenCreate);
  const [viewMode, setViewMode] = useState('table'); // 'table' or 'grid'
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (initialOpenCreate) {
      setShowCreateModal(true);
    }
  }, [initialOpenCreate]);

  // Only display active (non-closed) tickets in Support Tickets view
  const displayTickets = tickets.filter(t => t.status !== 'closed');
  const openTicketsCount = displayTickets.filter(t => t.status === 'open').length;
  const inProgressCount = displayTickets.filter(t => t.status === 'in_progress').length;
  const highPriorityCount = displayTickets.filter(t => t.priority === 'high').length;
  const unassignedCount = displayTickets.filter(t => !t.assigned_to).length;

  const onFormSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await handleCreateTicket(e);
      setShowCreateModal(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetFilters = () => {
    const defaultFilters = {
      sort_by: 'created_at',
      sort_order: 'desc',
      status_filter: '',
      priority_filter: '',
      assigned_to_filter: '',
      created_by_filter: '',
      search: ''
    };
    setTicketFilters(defaultFilters);
    fetchTickets(defaultFilters);
  };

  return (
    <div className="space-y-3 pb-12">
      {/* Static / Sticky Control Deck (Header + Stats + Filters) */}
      <div className="sticky top-0 z-20 bg-[#F4F7FB]/95 backdrop-blur-md pt-0.5 pb-2 space-y-2.5">
        {/* 1. Compact Header + Inline Metric Pills & Actions */}
        <div className="bg-white rounded-2xl p-4 md:px-5 md:py-3.5 border border-slate-200/80 shadow-xs flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
          {/* Left: Title & Subtitle */}
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-tr from-emerald-600 via-emerald-500 to-teal-500 text-white flex items-center justify-center shadow-xs flex-shrink-0">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z" />
              </svg>
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-base md:text-xl font-bold text-slate-900 tracking-tight">Support Tickets</h1>
                <span className="bg-emerald-50 text-emerald-700 font-bold text-[10px] md:text-[11px] px-2 py-0.5 rounded-full border border-emerald-200">
                  {displayTickets.length} Total
                </span>
              </div>
              <p className="text-[10px] md:text-[11px] text-slate-400 font-medium">Manage, triage, and track organization issues</p>
            </div>
          </div>

          {/* Center: Compact Status KPI Pills (Desktop) */}
          <div className="hidden sm:flex items-center gap-2 flex-wrap">
            {/* Pending / Unassigned Queue Pill */}
            <button
              type="button"
              onClick={() => {
                const newFilters = {
                  ...ticketFilters,
                  assigned_to_filter: ticketFilters.assigned_to_filter === 'unassigned' ? '' : 'unassigned'
                };
                setTicketFilters(newFilters);
                fetchTickets(newFilters);
              }}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-xl text-xs font-semibold border transition-all cursor-pointer ${
                ticketFilters.assigned_to_filter === 'unassigned'
                  ? 'bg-amber-100 border-amber-400 text-amber-900 shadow-2xs font-bold'
                  : unassignedCount > 0
                  ? 'bg-amber-50/80 border-amber-200 text-amber-800 hover:bg-amber-100'
                  : 'bg-slate-50 border-slate-200/70 text-slate-700 hover:bg-slate-100'
              }`}
            >
              <span className={`w-2 h-2 rounded-full ${unassignedCount > 0 ? 'bg-amber-500 animate-pulse' : 'bg-slate-300'}`}></span>
              <span>Pending / Unassigned:</span>
              <span className={`font-bold ${unassignedCount > 0 ? 'text-amber-900' : 'text-slate-900'}`}>{unassignedCount}</span>
            </button>

            <button
              type="button"
              onClick={() => {
                const newFilters = { ...ticketFilters, status_filter: ticketFilters.status_filter === 'open' ? '' : 'open', assigned_to_filter: '' };
                setTicketFilters(newFilters);
                fetchTickets(newFilters);
              }}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-xl text-xs font-semibold border transition-all cursor-pointer ${
                ticketFilters.status_filter === 'open' && !ticketFilters.assigned_to_filter
                  ? 'bg-blue-100 border-blue-400 text-blue-900 shadow-2xs font-bold'
                  : 'bg-slate-50 border-slate-200/70 text-slate-700 hover:bg-slate-100'
              }`}
            >
              <span className="w-2 h-2 rounded-full bg-blue-500"></span>
              <span>Open:</span>
              <span className="font-bold text-slate-900">{openTicketsCount}</span>
            </button>

            <button
              type="button"
              onClick={() => {
                const newFilters = { ...ticketFilters, status_filter: ticketFilters.status_filter === 'in_progress' ? '' : 'in_progress', assigned_to_filter: '' };
                setTicketFilters(newFilters);
                fetchTickets(newFilters);
              }}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-xl text-xs font-semibold border transition-all cursor-pointer ${
                ticketFilters.status_filter === 'in_progress'
                  ? 'bg-amber-100 border-amber-400 text-amber-900 shadow-2xs font-bold'
                  : 'bg-slate-50 border-slate-200/70 text-slate-700 hover:bg-slate-100'
              }`}
            >
              <span className="w-2 h-2 rounded-full bg-amber-500"></span>
              <span>In Progress:</span>
              <span className="font-bold text-slate-900">{inProgressCount}</span>
            </button>

            <button
              type="button"
              onClick={() => {
                const newFilters = { ...ticketFilters, priority_filter: ticketFilters.priority_filter === 'high' ? '' : 'high', assigned_to_filter: '' };
                setTicketFilters(newFilters);
                fetchTickets(newFilters);
              }}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-xl text-xs font-semibold border transition-all cursor-pointer ${
                ticketFilters.priority_filter === 'high'
                  ? 'bg-purple-100 border-purple-400 text-purple-900 shadow-2xs font-bold'
                  : 'bg-slate-50 border-slate-200/70 text-slate-700 hover:bg-slate-100'
              }`}
            >
              <span className="w-2 h-2 rounded-full bg-purple-500"></span>
              <span>High:</span>
              <span className="font-bold text-slate-900">{highPriorityCount}</span>
            </button>
          </div>

          {/* Right: Actions */}
          <div className="flex items-center gap-2 justify-between sm:justify-end">
            {/* Export Dropdown */}
            <div className="relative">
              <select
                onChange={(e) => {
                  if (e.target.value) {
                    downloadFile(`${API_URL}/api/export/tickets?format=${e.target.value}`, `tickets.${e.target.value}`);
                    e.target.value = '';
                  }
                }}
                className="bg-slate-50 hover:bg-slate-100 text-slate-700 border border-slate-200 text-xs font-semibold px-2.5 md:px-3 py-1.5 md:py-2 rounded-xl cursor-pointer transition-colors shadow-2xs focus:outline-none"
              >
                <option value="">Export Data</option>
                <option value="csv">CSV</option>
                <option value="excel">Excel</option>
                <option value="pdf">PDF</option>
                <option value="json">JSON</option>
              </select>
            </div>

            {/* Primary + Create Ticket Button */}
            <button
              onClick={() => setShowCreateModal(true)}
              className="bg-gradient-to-tr from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 active:scale-95 text-white text-xs font-bold px-3 md:px-3.5 py-1.5 md:py-2 rounded-xl shadow-xs shadow-emerald-200 flex items-center gap-1.5 transition-all duration-200 group"
            >
              <svg className="w-4 h-4 group-hover:rotate-90 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" />
              </svg>
              <span>Create Ticket</span>
            </button>
          </div>
        </div>

        {/* 2. Compact Modern Filter & Search Bar + Mobile Chips */}
        <div className="bg-white rounded-2xl p-2.5 md:p-3 border border-slate-200/80 shadow-xs space-y-2">
          {/* Mobile Horizontal Filter Chips */}
          <div className="flex sm:hidden items-center gap-1.5 overflow-x-auto pb-1 no-scrollbar text-xs">
            <button
              onClick={() => {
                const newFilters = { ...ticketFilters, status_filter: '', priority_filter: '', assigned_to_filter: '' };
                setTicketFilters(newFilters);
                fetchTickets(newFilters);
              }}
              className={`px-3 py-1 rounded-full font-bold whitespace-nowrap text-[11px] transition-all border ${
                !ticketFilters.status_filter && !ticketFilters.priority_filter && !ticketFilters.assigned_to_filter
                  ? 'bg-emerald-600 text-white border-emerald-600 shadow-2xs'
                  : 'bg-slate-100 text-slate-600 border-slate-200'
              }`}
            >
              All ({displayTickets.length})
            </button>
            <button
              onClick={() => {
                const newFilters = { ...ticketFilters, assigned_to_filter: ticketFilters.assigned_to_filter === 'unassigned' ? '' : 'unassigned', status_filter: '' };
                setTicketFilters(newFilters);
                fetchTickets(newFilters);
              }}
              className={`px-3 py-1 rounded-full font-bold whitespace-nowrap text-[11px] transition-all border ${
                ticketFilters.assigned_to_filter === 'unassigned'
                  ? 'bg-amber-600 text-white border-amber-600 shadow-2xs'
                  : 'bg-amber-50 text-amber-800 border-amber-200'
              }`}
            >
              Pending ({unassignedCount})
            </button>
            <button
              onClick={() => {
                const newFilters = { ...ticketFilters, status_filter: 'open', priority_filter: '', assigned_to_filter: '' };
                setTicketFilters(newFilters);
                fetchTickets(newFilters);
              }}
              className={`px-3 py-1 rounded-full font-bold whitespace-nowrap text-[11px] transition-all border ${
                ticketFilters.status_filter === 'open' && !ticketFilters.assigned_to_filter
                  ? 'bg-red-600 text-white border-red-600 shadow-2xs'
                  : 'bg-red-50 text-red-700 border-red-200'
              }`}
            >
              Open ({openTicketsCount})
            </button>
            <button
              onClick={() => {
                const newFilters = { ...ticketFilters, status_filter: 'in_progress', priority_filter: '', assigned_to_filter: '' };
                setTicketFilters(newFilters);
                fetchTickets(newFilters);
              }}
              className={`px-3 py-1 rounded-full font-bold whitespace-nowrap text-[11px] transition-all border ${
                ticketFilters.status_filter === 'in_progress'
                  ? 'bg-amber-600 text-white border-amber-600 shadow-2xs'
                  : 'bg-amber-50 text-amber-700 border-amber-200'
              }`}
            >
              In Progress ({inProgressCount})
            </button>
            <button
              onClick={() => {
                const newFilters = { ...ticketFilters, priority_filter: 'high', status_filter: '', assigned_to_filter: '' };
                setTicketFilters(newFilters);
                fetchTickets(newFilters);
              }}
              className={`px-3 py-1 rounded-full font-bold whitespace-nowrap text-[11px] transition-all border ${
                ticketFilters.priority_filter === 'high'
                  ? 'bg-purple-600 text-white border-purple-600 shadow-2xs'
                  : 'bg-purple-50 text-purple-700 border-purple-200'
              }`}
            >
              High Priority ({highPriorityCount})
            </button>
          </div>

          <div className="flex flex-col lg:flex-row items-stretch lg:items-center gap-2 md:gap-2.5">
            {/* Quick Search */}
            <div className="relative flex-1 min-w-[180px]">
              <svg className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                type="text"
                placeholder="Search by title, ID, creator..."
                className="w-full bg-slate-50 border border-slate-200 focus:border-emerald-500 focus:bg-white rounded-xl pl-9 pr-3 py-1.5 text-xs text-slate-800 placeholder-slate-400 focus:outline-none transition-all"
                value={ticketFilters.search || ''}
                onChange={(e) => {
                  const newFilters = { ...ticketFilters, search: e.target.value };
                  setTicketFilters(newFilters);
                  fetchTickets(newFilters);
                }}
              />
            </div>

            {/* Inline Filter Selects */}
            <div className="flex items-center gap-2 flex-wrap sm:flex-nowrap">
              {/* Priority Filter */}
              <select
                className="bg-slate-50 border border-slate-200 text-slate-700 rounded-xl px-2.5 py-1.5 text-xs font-medium focus:outline-none focus:border-blue-500 min-w-[110px]"
                value={ticketFilters.priority_filter || ''}
                onChange={(e) => {
                  const newFilters = { ...ticketFilters, priority_filter: e.target.value };
                  setTicketFilters(newFilters);
                  fetchTickets(newFilters);
                }}
              >
                <option value="">Priority: All</option>
                <option value="high">High Priority</option>
                <option value="medium">Medium Priority</option>
                <option value="low">Low Priority</option>
              </select>

              {/* Assigned Agent Filter */}
              <select
                className={`border rounded-xl px-2.5 py-1.5 text-xs font-medium focus:outline-none focus:border-blue-500 min-w-[130px] max-w-[160px] truncate ${
                  ticketFilters.assigned_to_filter === 'unassigned'
                    ? 'bg-amber-50 border-amber-300 text-amber-900 font-bold'
                    : 'bg-slate-50 border-slate-200 text-slate-700'
                }`}
                value={ticketFilters.assigned_to_filter || ''}
                onChange={(e) => {
                  const newFilters = { ...ticketFilters, assigned_to_filter: e.target.value };
                  setTicketFilters(newFilters);
                  fetchTickets(newFilters);
                }}
              >
                <option value="">Agent: All</option>
                <option value="unassigned">⏳ Pending / Unassigned</option>
                {assignableUsers.map(u => (
                  <option key={u.id} value={u.id}>{u.username}</option>
                ))}
              </select>

              {/* Sort Filter */}
              <select
                className="bg-slate-50 border border-slate-200 text-slate-700 rounded-xl px-2.5 py-1.5 text-xs font-medium focus:outline-none focus:border-blue-500 min-w-[110px]"
                value={ticketFilters.sort_by || 'created_at'}
                onChange={(e) => {
                  const newFilters = { ...ticketFilters, sort_by: e.target.value };
                  setTicketFilters(newFilters);
                  fetchTickets(newFilters);
                }}
              >
                <option value="created_at">Date Created</option>
                <option value="priority">Priority</option>
                <option value="title">Title</option>
              </select>

              {/* Sort Order */}
              <select
                className="bg-slate-50 border border-slate-200 text-slate-700 rounded-xl px-2.5 py-1.5 text-xs font-medium focus:outline-none focus:border-blue-500 min-w-[90px]"
                value={ticketFilters.sort_order || 'desc'}
                onChange={(e) => {
                  const newFilters = { ...ticketFilters, sort_order: e.target.value };
                  setTicketFilters(newFilters);
                  fetchTickets(newFilters);
                }}
              >
                <option value="desc">Newest</option>
                <option value="asc">Oldest</option>
              </select>

              {/* Reset Button */}
              <button
                onClick={resetFilters}
                className="p-1.5 bg-slate-50 hover:bg-slate-100 text-slate-500 hover:text-slate-800 border border-slate-200 rounded-xl transition-colors shadow-2xs"
                title="Reset all filters"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              </button>

              {/* View Mode Toggle */}
              <div className="bg-slate-100 p-0.5 rounded-xl flex items-center gap-0.5">
                <button
                  onClick={() => setViewMode('table')}
                  className={`p-1.5 rounded-lg text-xs transition-colors ${
                    viewMode === 'table' ? 'bg-white text-blue-600 shadow-2xs' : 'text-slate-500 hover:text-slate-800'
                  }`}
                  title="Table View"
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                  </svg>
                </button>
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-1.5 rounded-lg text-xs transition-colors ${
                    viewMode === 'grid' ? 'bg-white text-blue-600 shadow-2xs' : 'text-slate-500 hover:text-slate-800'
                  }`}
                  title="Cards View"
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 3. Tickets Content (Table or Grid View) */}
      {/* Mobile View: High-Density Material 3 Ticket Cards */}
      <div className="block md:hidden space-y-2.5">
        {displayTickets.map((ticket) => (
          <div
            key={ticket.id}
            onClick={() => handleTicketClick(ticket)}
            className="bg-white rounded-2xl p-3.5 border border-slate-200/90 shadow-2xs active:scale-[0.99] transition-all space-y-2.5"
          >
            {/* Header: ID + Priority + Status */}
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-1.5 min-w-0">
                <span className="text-[10px] font-mono font-bold text-emerald-800 bg-emerald-50 px-1.5 py-0.5 rounded border border-emerald-200">
                  {ticket.ticket_number || (ticket.id ? `#${ticket.id.slice(0, 8)}` : 'TKT')}
                </span>
                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold capitalize ${
                  ticket.priority === 'high' ? 'bg-red-50 text-red-700 border border-red-200' :
                  ticket.priority === 'medium' ? 'bg-amber-50 text-amber-700 border border-amber-200' :
                  'bg-emerald-50 text-emerald-700 border border-emerald-200'
                }`}>
                  <span className={`w-1.5 h-1.5 rounded-full ${
                    ticket.priority === 'high' ? 'bg-red-500' :
                    ticket.priority === 'medium' ? 'bg-amber-500' : 'bg-emerald-500'
                  }`} />
                  {ticket.priority || 'medium'}
                </span>
              </div>

              <div className="flex items-center gap-1.5">
                <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold capitalize ${
                  ticket.status === 'open' ? 'bg-emerald-50 text-emerald-800 border border-emerald-200' :
                  ticket.status === 'in_progress' ? 'bg-amber-50 text-amber-800 border border-amber-200' :
                  'bg-slate-100 text-slate-700 border border-slate-200'
                }`}>
                  {ticket.status === 'open' ? 'Open' : ticket.status === 'in_progress' ? 'In Progress' : 'Closed'}
                </span>
              </div>
            </div>

            {/* Title & Description */}
            <div className="min-w-0">
              <div className="flex items-center gap-1.5">
                <h3 className="font-bold text-slate-900 text-xs leading-snug truncate">
                  {ticket.title}
                </h3>
                {ticket.media_url && (
                  <span className="p-0.5 text-emerald-700 bg-emerald-50 border border-emerald-200 rounded flex-shrink-0" title="Attachment attached">
                    <svg className="w-2.5 h-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                    </svg>
                  </span>
                )}
              </div>
              {ticket.description && (
                <p className="text-[11px] text-slate-500 line-clamp-2 mt-0.5 leading-tight">
                  {ticket.description}
                </p>
              )}
            </div>

            {/* Reporter, Assignee & Quick Actions */}
            <div className="pt-2 border-t border-slate-100 flex items-center justify-between gap-2 text-[11px]">
              <div className="text-slate-500 font-medium truncate text-[10px] flex items-center gap-1.5">
                <span className="inline-flex items-center gap-0.5 text-slate-600">
                  <svg className="w-3 h-3 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                  <span>{ticket.created_by_username || 'User'}</span>
                </span>
                {ticket.assigned_to_username ? (
                  <span className="text-slate-400 inline-flex items-center gap-0.5">
                    <span>→</span>
                    <svg className="w-3 h-3 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                    </svg>
                    <span className="text-slate-600 font-semibold">{ticket.assigned_to_username}</span>
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-bold bg-amber-50 text-amber-800 border border-amber-200">
                    <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse"></span>
                    Pending
                  </span>
                )}
              </div>

              <div className="flex items-center gap-1 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                {!ticket.assigned_to && (
                  <button
                    onClick={() => setSelectedTicket(ticket)}
                    className="px-2 py-1 text-[10px] font-bold text-amber-800 bg-amber-100 hover:bg-amber-600 hover:text-white rounded-lg active:scale-95 transition-all border border-amber-300"
                  >
                    Assign
                  </button>
                )}
                <button
                  onClick={() => {
                    setTicketForNote(ticket);
                    setShowNoteModal(true);
                  }}
                  className="px-2 py-1 text-[10px] font-bold text-indigo-700 bg-indigo-50 border border-indigo-200 rounded-lg active:scale-95 transition-all flex items-center gap-0.5"
                >
                  <span>Note</span>
                  {ticket.notes?.length > 0 && <span>({ticket.notes.length})</span>}
                </button>
                <button
                  onClick={() => {
                    setTicketToClose(ticket);
                    setShowCloseModal(true);
                  }}
                  className="px-2 py-1 text-[10px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg active:scale-95 transition-all"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Desktop View: Table / Grid */}
      {viewMode === 'table' ? (
        <div className="hidden md:block bg-white rounded-2xl border border-slate-200/80 shadow-xs overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead className="bg-slate-50 text-slate-400 font-bold uppercase tracking-wider text-[10px] border-b border-slate-100">
                <tr>
                  <th className="py-2.5 px-4 md:px-5">Ticket & Details</th>
                  <th className="py-2.5 px-3">Priority</th>
                  <th className="py-2.5 px-3">Status</th>
                  <th className="py-2.5 px-3">Reporter & Assignee</th>
                  <th className="py-2.5 px-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {displayTickets.map((ticket) => (
                  <tr
                    key={ticket.id}
                    onClick={() => handleTicketClick(ticket)}
                    className="hover:bg-slate-50/70 transition-colors cursor-pointer group"
                  >
                    <td className="py-3 px-4 md:px-5">
                      <div className="flex items-start gap-2.5">
                        <div className={`w-2 h-2 mt-1 rounded-full flex-shrink-0 ${
                          ticket.priority === 'high' ? 'bg-red-500' :
                          ticket.priority === 'medium' ? 'bg-amber-500' : 'bg-emerald-500'
                        }`} />
                        <div className="min-w-0">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <span className="text-[10px] font-mono font-bold text-blue-600 bg-blue-50 px-1 py-0.2 rounded">
                              {ticket.ticket_number || (ticket.id ? `#${ticket.id.slice(0, 8)}` : 'TKT')}
                            </span>
                            <span className="font-bold text-slate-800 group-hover:text-blue-600 transition-colors truncate">
                              {ticket.title}
                            </span>
                            {ticket.media_url && (
                              <span className="text-[10px] font-semibold text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded flex items-center gap-1">
                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                                </svg>
                                <span>File</span>
                              </span>
                            )}
                          </div>
                          <p className="text-[11px] text-slate-400 truncate mt-0.5 max-w-sm md:max-w-md">
                            {ticket.description || 'No description'}
                          </p>
                        </div>
                      </div>
                    </td>

                    <td className="py-3 px-3">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold capitalize ${
                        ticket.priority === 'high' ? 'bg-red-50 text-red-600 border border-red-100' :
                        ticket.priority === 'medium' ? 'bg-amber-50 text-amber-600 border border-amber-100' :
                        'bg-emerald-50 text-emerald-600 border border-emerald-100'
                      }`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${
                          ticket.priority === 'high' ? 'bg-red-500' :
                          ticket.priority === 'medium' ? 'bg-amber-500' : 'bg-emerald-500'
                        }`} />
                        {ticket.priority || 'medium'}
                      </span>
                    </td>

                    <td className="py-3 px-3">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold capitalize ${
                        ticket.status === 'open' ? 'bg-blue-50 text-blue-600 border border-blue-100' :
                        ticket.status === 'in_progress' ? 'bg-amber-50 text-amber-600 border border-amber-100' :
                        'bg-emerald-50 text-emerald-600 border border-emerald-100'
                      }`}>
                        {ticket.status === 'open' ? 'Open' : ticket.status === 'in_progress' ? 'In Progress' : 'Closed'}
                      </span>
                    </td>

                    <td className="py-3 px-3">
                      <div className="text-xs text-slate-700 font-medium truncate flex items-center gap-1.5">
                        <span>{ticket.created_by_username || 'User'}</span>
                        {ticket.assigned_to_username ? (
                          <span className="text-slate-500 font-normal">→ <strong className="text-slate-800 font-semibold">{ticket.assigned_to_username}</strong></span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold bg-amber-50 text-amber-800 border border-amber-200">
                            <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse"></span>
                            Pending Assignment
                          </span>
                        )}
                      </div>
                      <div className="text-[10px] text-slate-400">
                        {ticket.created_at ? format(new Date(ticket.created_at), 'MMM dd, yyyy') : 'Recent'}
                      </div>
                    </td>

                    <td className="py-3 px-4 text-right" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center justify-end gap-1.5">
                        {!ticket.assigned_to ? (
                          <button
                            onClick={() => setSelectedTicket(ticket)}
                            className="px-2.5 py-1 text-[11px] font-bold text-amber-800 bg-amber-50 hover:bg-amber-600 hover:text-white rounded-lg transition-colors border border-amber-300 shadow-2xs"
                          >
                            Assign Now
                          </button>
                        ) : (
                          <button
                            onClick={() => setSelectedTicket(ticket)}
                            className="px-2 py-1 text-[11px] font-semibold text-blue-600 hover:text-white hover:bg-blue-600 rounded-lg transition-colors border border-blue-200 hover:border-blue-600"
                          >
                            Edit
                          </button>
                        )}
                        <button
                          onClick={() => {
                            setTicketForNote(ticket);
                            setShowNoteModal(true);
                          }}
                          className="px-2 py-1 text-[11px] font-semibold text-indigo-600 hover:text-white hover:bg-indigo-600 rounded-lg transition-colors border border-indigo-200 hover:border-indigo-600 flex items-center gap-1"
                        >
                          <span>Note</span>
                          {ticket.notes?.length > 0 && (
                            <span className="w-4 h-4 rounded-full bg-indigo-100 text-indigo-700 text-[9px] font-bold flex items-center justify-center">
                              {ticket.notes.length}
                            </span>
                          )}
                        </button>
                        <button
                          onClick={() => {
                            setTicketToClose(ticket);
                            setShowCloseModal(true);
                          }}
                          className="px-2 py-1 text-[11px] font-semibold text-emerald-600 hover:text-white hover:bg-emerald-600 rounded-lg transition-colors border border-emerald-200 hover:border-emerald-600"
                        >
                          Close
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {displayTickets.length === 0 && (
            <div className="text-center py-12 px-4">
              <div className="w-12 h-12 rounded-2xl bg-slate-100 text-slate-400 flex items-center justify-center mx-auto mb-2.5">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                </svg>
              </div>
              <h3 className="text-sm font-bold text-slate-800">No tickets found</h3>
              <p className="text-xs text-slate-400 mt-0.5">
                No tickets matching your filter criteria.
              </p>
              <button
                onClick={() => setShowCreateModal(true)}
                className="mt-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold text-xs px-3.5 py-1.5 rounded-xl shadow-xs"
              >
                + Create Ticket
              </button>
            </div>
          )}
        </div>
      ) : (
        /* Grid Cards View */
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {displayTickets.map((ticket) => (
            <div
              key={ticket.id}
              onClick={() => handleTicketClick(ticket)}
              className="bg-white rounded-2xl p-4 border border-slate-200/80 shadow-xs hover:shadow-md transition-all duration-200 flex flex-col justify-between cursor-pointer group"
            >
              <div>
                <div className="flex items-center justify-between gap-2 mb-2">
                  <span className="text-[10px] font-mono font-bold text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded">
                    {ticket.ticket_number || (ticket.id ? `#${ticket.id.slice(0, 8)}` : 'TKT')}
                  </span>
                  <div className="flex items-center gap-1">
                    <span className={`px-1.5 py-0.2 rounded-full text-[10px] font-semibold capitalize ${
                      ticket.priority === 'high' ? 'bg-red-50 text-red-600' :
                      ticket.priority === 'medium' ? 'bg-amber-50 text-amber-600' :
                      'bg-emerald-50 text-emerald-600'
                    }`}>
                      {ticket.priority || 'medium'}
                    </span>
                    <span className={`px-1.5 py-0.2 rounded-full text-[10px] font-semibold capitalize ${
                      ticket.status === 'open' ? 'bg-blue-50 text-blue-600' :
                      ticket.status === 'in_progress' ? 'bg-amber-50 text-amber-600' :
                      'bg-emerald-50 text-emerald-600'
                    }`}>
                      {ticket.status}
                    </span>
                  </div>
                </div>

                <h3 className="font-bold text-slate-800 text-xs md:text-sm group-hover:text-blue-600 transition-colors line-clamp-1">
                  {ticket.title}
                </h3>
                <p className="text-[11px] text-slate-400 mt-1 line-clamp-2">
                  {ticket.description || 'No description provided'}
                </p>
              </div>

              <div className="mt-3 pt-2.5 border-t border-slate-100 flex items-center justify-between text-xs text-slate-500">
                <div className="text-[10px] text-slate-400">
                  By <span className="font-semibold text-slate-700">{ticket.created_by_username}</span>
                  {ticket.created_at && ` • ${format(new Date(ticket.created_at), 'MMM dd')}`}
                </div>

                <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                  <button
                    onClick={() => setSelectedTicket(ticket)}
                    className="p-1 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                    title="Edit"
                  >
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                  </button>
                  <button
                    onClick={() => {
                      setTicketForNote(ticket);
                      setShowNoteModal(true);
                    }}
                    className="p-1 text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors relative"
                    title="Add Note"
                  >
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
                    </svg>
                    {ticket.notes?.length > 0 && (
                      <span className="absolute -top-1 -right-1 w-3 h-3 bg-indigo-600 text-white rounded-full text-[8px] font-bold flex items-center justify-center">
                        {ticket.notes.length}
                      </span>
                    )}
                  </button>
                  <button
                    onClick={() => {
                      setTicketToClose(ticket);
                      setShowCloseModal(true);
                    }}
                    className="p-1 text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors"
                    title="Close"
                  >
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* 4. Compact & Clean Create Ticket Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-slate-900/60 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="fixed inset-0" onClick={() => setShowCreateModal(false)} />

          <div className="relative w-full max-w-xl bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden z-10 flex flex-col max-h-[92vh] animate-fadeIn">
            {/* Modal Header */}
            <div className="p-4 md:px-5 md:py-4 border-b border-slate-100 flex items-center justify-between bg-gradient-to-r from-blue-600 to-indigo-600 text-white">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-xl bg-white/20 backdrop-blur-xs text-white flex items-center justify-center">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-base font-bold text-white leading-tight">Create Support Ticket</h3>
                  <p className="text-[11px] text-blue-100">Submit a new request or issue</p>
                </div>
              </div>

              <button
                onClick={() => setShowCreateModal(false)}
                className="w-7 h-7 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors text-xs"
              >
                ✕
              </button>
            </div>

            {/* Form Body */}
            <form onSubmit={onFormSubmit} className="flex-1 overflow-y-auto p-4 md:p-5 space-y-3.5">
              {/* Title */}
              <div>
                <label className="block text-[11px] font-bold text-slate-700 uppercase tracking-wider mb-1">
                  Issue Title <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  placeholder="e.g. WiFi connectivity issue in Meeting Room 3"
                  className="w-full bg-slate-50 border border-slate-200 focus:border-blue-500 focus:bg-white rounded-xl px-3.5 py-2 text-xs md:text-sm text-slate-800 placeholder-slate-400 focus:outline-none transition-all shadow-2xs"
                  value={ticketForm.title}
                  onChange={(e) => setTicketForm({ ...ticketForm, title: e.target.value })}
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-[11px] font-bold text-slate-700 uppercase tracking-wider mb-1">
                  Description <span className="text-red-500">*</span>
                </label>
                <textarea
                  required
                  rows={3}
                  placeholder="Describe the issue, what happened, or relevant error codes..."
                  className="w-full bg-slate-50 border border-slate-200 focus:border-blue-500 focus:bg-white rounded-xl p-3 text-xs md:text-sm text-slate-800 placeholder-slate-400 focus:outline-none transition-all shadow-2xs resize-none"
                  value={ticketForm.description}
                  onChange={(e) => setTicketForm({ ...ticketForm, description: e.target.value })}
                />
              </div>

              {/* Priority Radio Cards */}
              <div>
                <label className="block text-[11px] font-bold text-slate-700 uppercase tracking-wider mb-1">
                  Priority Level
                </label>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { value: 'low', label: 'Low', desc: 'Minor / General', dot: 'bg-emerald-500' },
                    { value: 'medium', label: 'Medium', desc: 'Standard priority', dot: 'bg-amber-500' },
                    { value: 'high', label: 'High', desc: 'Urgent blocker', dot: 'bg-red-500' }
                  ].map((p) => (
                    <div
                      key={p.value}
                      onClick={() => setTicketForm({ ...ticketForm, priority: p.value })}
                      className={`p-2.5 rounded-xl border-2 cursor-pointer transition-all ${
                        ticketForm.priority === p.value
                          ? 'border-blue-600 bg-blue-50/40 shadow-xs'
                          : 'border-slate-200 hover:border-slate-300 bg-white'
                      }`}
                    >
                      <div className="flex items-center gap-1.5 mb-0.5">
                        <span className={`w-2 h-2 rounded-full ${p.dot}`}></span>
                        <span className="font-bold text-xs text-slate-800">{p.label}</span>
                      </div>
                      <p className="text-[10px] text-slate-400 truncate">{p.desc}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Assign To (Optional) */}
              <div>
                <label className="block text-[11px] font-bold text-slate-700 uppercase tracking-wider mb-1">
                  Assign to Agent (Optional)
                </label>
                <select
                  className="w-full bg-slate-50 border border-slate-200 focus:border-blue-500 focus:bg-white rounded-xl px-3.5 py-2 text-xs md:text-sm text-slate-800 focus:outline-none transition-all"
                  value={ticketForm.assigned_to || ''}
                  onChange={(e) => setTicketForm({ ...ticketForm, assigned_to: e.target.value })}
                >
                  <option value="">Auto-assign to best available agent</option>
                  {assignableUsers.map(u => (
                    <option key={u.id} value={u.id}>{u.username} ({u.role})</option>
                  ))}
                </select>
              </div>

              {/* File Attachment */}
              <div>
                <label className="block text-[11px] font-bold text-slate-700 uppercase tracking-wider mb-1">
                  Attachment (Optional)
                </label>
                <div className={`relative border-2 border-dashed rounded-xl p-3 text-center transition-all ${
                  selectedFile ? 'border-emerald-400 bg-emerald-50/40' : 'border-slate-200 hover:border-blue-400 hover:bg-blue-50/30'
                }`}>
                  {!selectedFile ? (
                    <div>
                      <svg className="mx-auto h-5 w-5 text-slate-400 mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      <span className="text-xs font-semibold text-slate-700">Upload screenshot or file</span>
                      <p className="text-[10px] text-slate-400">PNG, JPG, PDF up to 10MB</p>
                      <input
                        type="file"
                        accept="image/*,.pdf"
                        onChange={(e) => setSelectedFile(e.target.files[0])}
                        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                      />
                    </div>
                  ) : (
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <svg className="w-4 h-4 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <div className="text-left">
                          <div className="text-xs font-bold text-slate-800 truncate max-w-xs">{selectedFile.name}</div>
                          <div className="text-[10px] text-slate-400">{(selectedFile.size / 1024 / 1024).toFixed(2)} MB</div>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => setSelectedFile(null)}
                        className="text-xs font-semibold text-red-600 hover:bg-red-50 px-2 py-0.5 rounded-lg transition-colors"
                      >
                        Remove
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Modal Footer */}
              <div className="pt-3 border-t border-slate-100 flex items-center justify-end gap-2.5">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="px-3.5 py-2 rounded-xl border border-slate-200 text-xs font-semibold text-slate-600 hover:bg-slate-100 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="px-4 py-2 rounded-xl bg-gradient-to-tr from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white text-xs font-bold shadow-xs transition-colors flex items-center gap-1.5 disabled:opacity-50"
                >
                  {isSubmitting ? (
                    <>
                      <span className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                      <span>Submitting...</span>
                    </>
                  ) : (
                    <span>Submit Ticket</span>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Mobile Floating Action Button (FAB) for Instant Ticket Creation */}
      <button
        onClick={() => setShowCreateModal(true)}
        className="md:hidden fixed bottom-20 right-4 z-30 w-12 h-12 rounded-full bg-gradient-to-tr from-emerald-600 via-emerald-500 to-teal-500 text-white shadow-xl shadow-emerald-600/30 flex items-center justify-center active:scale-90 transition-transform"
        title="Create New Ticket"
      >
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" />
        </svg>
      </button>
    </div>
  );
};

export default TicketsManagement;
